import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { XMarkIcon, PlayIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

function ActiveSessionAlert() {
    const [activeSession, setActiveSession] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [elapsedTime, setElapsedTime] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // Don't show on the active session page itself
    const isOnActiveSessionPage = location.pathname === '/active-session';

    const checkActiveSession = async () => {
        try {
            const response = await axios.get('/api/sessions/', {
                params: { status: 'active' }
            });
            const sessions = response.data?.results || response.data || [];

            if (sessions.length > 0) {
                setActiveSession(sessions[0]);
                setIsVisible(true);
            } else {
                setActiveSession(null);
                setIsVisible(false);
            }
        } catch (error) {
            console.error('Error checking active session:', error);
        }
    };

    useEffect(() => {
        // Check for active session on component mount
        checkActiveSession();

        // Set up periodic checking every 5 seconds (faster refresh for better UX)
        const interval = setInterval(checkActiveSession, 5000);

        // Listen for session-ended event for immediate refresh
        const handleSessionEnded = () => {
            setActiveSession(null);
            setIsVisible(false);
            checkActiveSession();
        };
        window.addEventListener('session-ended', handleSessionEnded);

        return () => {
            clearInterval(interval);
            window.removeEventListener('session-ended', handleSessionEnded);
        };
    }, []);

    // Listen for route changes and recheck (catches when session ends and navigates away)
    useEffect(() => {
        checkActiveSession();
    }, [location.pathname]);

    // Update elapsed time every second
    useEffect(() => {
        if (activeSession && activeSession.status === 'active' && activeSession.start_time) {
            const updateElapsedTime = () => {
                const start = new Date(activeSession.start_time);
                const now = new Date();
                const diff = now - start;

                const hours = Math.floor(diff / 3600000);
                const minutes = Math.floor((diff % 3600000) / 60000);
                const seconds = Math.floor((diff % 60000) / 1000);

                setElapsedTime(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
            };

            updateElapsedTime();
            const interval = setInterval(updateElapsedTime, 1000);

            return () => clearInterval(interval);
        }
    }, [activeSession]);

    // Add/remove top margin to main content when alert is visible
    useEffect(() => {
        const mainElement = document.querySelector('main');
        if (mainElement) {
            if (isVisible && !isDismissed && activeSession && !isOnActiveSessionPage) {
                mainElement.style.marginTop = '4rem'; // 64px to account for alert height
            } else {
                mainElement.style.marginTop = '0';
            }
        }

        return () => {
            if (mainElement) {
                mainElement.style.marginTop = '0';
            }
        };
    }, [isVisible, isDismissed, activeSession, isOnActiveSessionPage]);

    const handleDismiss = async () => {
        // Refetch to confirm session is really gone before dismissing
        try {
            const response = await axios.get('/api/sessions/', {
                params: { status: 'active' }
            });
            const sessions = response.data?.results || response.data || [];

            if (sessions.length > 0) {
                // Session still exists, just dismiss the alert
                setIsDismissed(true);

                // Reset dismissal after 5 minutes to remind user
                setTimeout(() => {
                    setIsDismissed(false);
                }, 300000);
            } else {
                // No active session, clear everything
                setActiveSession(null);
                setIsVisible(false);
            }
        } catch (error) {
            console.error('Error checking active session:', error);
            // On error, just dismiss normally
            setIsDismissed(true);
            setTimeout(() => {
                setIsDismissed(false);
            }, 300000);
        }
    };

    const handleGoToSession = () => {
        navigate('/active-session');
    };

    // Don't show if dismissed, no active session, or on the active session page
    if (!isVisible || isDismissed || !activeSession || isOnActiveSessionPage) {
        return null;
    }

    return (
        <div className="fixed top-0 left-0 right-0 z-50 bg-green-600 dark:bg-green-700 text-white px-4 py-3 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                    <PlayIcon className="h-5 w-5 animate-pulse" />
                    <div className="flex-1">
                        <p className="text-sm sm:text-base font-medium">
                            Active Session: {activeSession.location?.name} • {activeSession.stake?.name}
                            <span className="ml-3 font-mono">{elapsedTime}</span>
                            <span className="ml-3">Buy-in: ${parseFloat(activeSession.current_buy_in || 0).toFixed(2)}</span>
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                    <button
                        onClick={handleGoToSession}
                        className="px-3 py-1 bg-green-700 dark:bg-green-800 hover:bg-green-800 dark:hover:bg-green-900 rounded text-xs sm:text-sm font-medium transition-colors"
                    >
                        View Session
                    </button>
                    <button
                        onClick={handleDismiss}
                        className="p-1 hover:bg-green-700 dark:hover:bg-green-800 rounded transition-colors"
                        title="Dismiss for 5 minutes"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ActiveSessionAlert;
